import { useCallback, useMemo, useState } from "react";
import { CellContext, ColumnDef } from "@tanstack/react-table";
import { useParams } from "react-router-dom";
import { useOutletContext } from "react-router-dom";
import { ActivityTypeOutletContext } from "../_layout";
import { ParticipantRow } from "../types";
import ParticipantsTable from "../_components/ParticipantsTable";
import AddTypeLevelParticipationModal from "../_components/AddTypeLevelParticipationModal";
import { isActivityType } from "../../_utils";
import {
  appendManagingSectionColumn,
  getManagingSectionExportValue,
  MANAGING_SECTION_EXPORT_HEADER
} from "../participantTableColumns";
import { SectionAvailability } from "../../types";
import { toFormat } from "@/lib/dateHelpers";
import { uniqBy } from "lodash";
import { useFlags } from "@/hooks/useFeatureFlags";
import useActivityTypePermissions from "@/hooks/useActivityTypePermissions";
import useAmplitude from "@/hooks/useAmplitude";
import {
  PDR_ANALYTICS_EVENTS,
  getAddParticipationFlowStartedAnalyticsData
} from "@/features/participation/_utils/amplitude";

export default function ParticipantsPage() {
  const { activityTypeId = "" } = useParams();
  const { activityData } = useOutletContext<ActivityTypeOutletContext>();
  const flags = useFlags();
  const typeLevelEnabled = !!flags["backoffice-type-level-participations"];
  const canManageParticipations = useActivityTypePermissions(activityData || {});
  const { trackEvent } = useAmplitude();
  const [showAddModal, setShowAddModal] = useState(false);
  const [participationFlowStartedAtMs, setParticipationFlowStartedAtMs] = useState<number | null>(null);

  const openAddTypeLevelParticipationModal = useCallback(() => {
    const startedAt = Date.now();
    setParticipationFlowStartedAtMs(startedAt);
    trackEvent(
      PDR_ANALYTICS_EVENTS.addParticipationStarted,
      getAddParticipationFlowStartedAnalyticsData({
        source: "activity_type_participants",
        participationKind: "type-level"
      })
    );
    setShowAddModal(true);
  }, [trackEvent]);

  const closeAddTypeLevelParticipationModal = () => {
    setShowAddModal(false);
    setParticipationFlowStartedAtMs(null);
  };

  const isInstructorLedType = isActivityType(activityData?.title, "instructor-led");
  const showSelfReportedColumns = isActivityType(activityData?.title, "education-self-reported") || isInstructorLedType;
  const creditRates = activityData?.config?.creditRates ?? [];
  const hasMultipleRoles = uniqBy(creditRates, "participationRole").length > 1;
  const hasMultipleAttendanceTypes = uniqBy(creditRates, "attendanceType").length > 1;
  const allowsParticipation = !!activityData?.allowsParticipation;
  const showAddButton = typeLevelEnabled && allowsParticipation && canManageParticipations;
  const sectionAvailability = activityData?.sectionAvailability;
  const showManagingSectionColumn = sectionAvailability === SectionAvailability.CAN_CREATE;

  const extraColumns: ColumnDef<ParticipantRow, unknown>[] = useMemo(() => {
    const titleColumn: ColumnDef<ParticipantRow, unknown> = {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }: CellContext<ParticipantRow, unknown>) => <div>{row.original.title}</div>,
      size: 300
    };

    if (showSelfReportedColumns) {
      return appendManagingSectionColumn(
        [
          titleColumn,
          { accessorKey: "startDate", header: "Start Date", enableSorting: false, size: 150 },
          { accessorKey: "endDate", header: "End Date", enableSorting: false, size: 150 },
          { accessorKey: "submittedAt", header: "Submitted", size: 150 },
          {
            accessorKey: "hoursServed",
            header: "Hours Served",
            cell: ({ row }: CellContext<ParticipantRow, unknown>) => row.original.hoursServed ?? "--",
            enableSorting: false,
            size: 150
          },
          { accessorKey: "creditsAwarded", header: "PDR Credits Awarded", size: 150 }
        ],
        sectionAvailability
      );
    }

    return appendManagingSectionColumn(
      [
        titleColumn,
        ...(hasMultipleRoles ? [{ accessorKey: "role", header: "Role", size: 300 }] : []),
        ...(hasMultipleAttendanceTypes
          ? [{ accessorKey: "attendanceType", header: "Attendance Type", size: 150 }]
          : []),
        { accessorKey: "startDate", header: "Start Date", size: 150 },
        { accessorKey: "endDate", header: "End Date", enableSorting: false, size: 150 },
        { accessorKey: "submittedAt", header: "Submitted", size: 150 },
        { accessorKey: "creditsAwarded", header: "PDR Credits Awarded", size: 150 }
      ],
      sectionAvailability
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSelfReportedColumns, creditRates, sectionAvailability]);

  const exportHeaders = useMemo(() => {
    const base = showSelfReportedColumns
      ? ["PGA ID", "Name", "Title", "Start Date", "End Date", "Submitted", "Hours Served", "PDR Credits Awarded"]
      : [
          "PGA ID",
          "Name",
          "Title",
          "Role",
          "Attendance Type",
          "Start Date",
          "End Date",
          "Submitted",
          "PDR Credits Awarded"
        ];

    return showManagingSectionColumn ? [...base, MANAGING_SECTION_EXPORT_HEADER] : base;
  }, [showSelfReportedColumns, showManagingSectionColumn]);

  const exportRow = useMemo(
    () =>
      (participant: ParticipantRow): (string | number | null | undefined)[] => {
        const base = [participant.pgaId, participant.name, participant.title];
        if (showSelfReportedColumns) {
          const row = [
            ...base,
            participant.startDate ? toFormat(participant.startDate) : "",
            participant.endDate ? toFormat(participant.endDate) : "",
            toFormat(participant.submittedAt),
            participant.hoursServed?.toString() ?? "",
            participant.creditsAwarded?.toString() ?? ""
          ];

          return showManagingSectionColumn ? [...row, getManagingSectionExportValue(participant)] : row;
        }

        const row = [
          ...base,
          participant.role,
          participant.attendanceType,
          participant.startDate ? toFormat(participant.startDate) : "",
          participant.endDate ? toFormat(participant.endDate) : "",
          new Date(participant.submittedAt)
            .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            .replace(",", ""),
          participant.creditsAwarded?.toString() ?? ""
        ];

        return showManagingSectionColumn ? [...row, getManagingSectionExportValue(participant)] : row;
      },
    [showSelfReportedColumns, showManagingSectionColumn]
  );

  const additionalHeadingActions = useMemo(
    () => [
      ...(showAddButton
        ? [
            {
              label: "Add Participants",
              icon: "add" as const,
              variant: "filled" as const,
              onClick: openAddTypeLevelParticipationModal
            }
          ]
        : [])
    ],
    [showAddButton, openAddTypeLevelParticipationModal]
  );

  return (
    <>
      <ParticipantsTable
        staticFilters={{ activityTypeId }}
        extraColumns={extraColumns}
        headingBody="View all participations across all activities of this type."
        additionalHeadingActions={additionalHeadingActions}
        exportHeaders={exportHeaders}
        exportRow={exportRow}
        activityTypeId={activityTypeId}
        testId="participants-table"
        noDataCaption="This activity type does not have any participants."
        persistDateRange={true}
        showAttendanceTypeFilter={hasMultipleAttendanceTypes}
        showRoleFilter={hasMultipleRoles}
        canManageParticipationActions={canManageParticipations}
      />

      {showAddModal && (
        <AddTypeLevelParticipationModal
          onDismiss={closeAddTypeLevelParticipationModal}
          activityTypeId={activityTypeId}
          creditRates={creditRates}
          participationFlowStartedAtMs={participationFlowStartedAtMs}
        />
      )}
    </>
  );
}
